import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { IconX as X } from '@tabler/icons-react';
import { fetchUserSessions, revokeSession, revokeUserSessions } from '@/api/adminApi';
import SessionsList from '@/components/SessionsList';
import { smallBtn } from './shared';

export default function UserSessionsModal({ email, onClose }: { email: string; onClose: () => void }) {
    const queryClient = useQueryClient();
    const queryKey = ['admin-user-sessions', email];
    const { data, isLoading } = useQuery({ queryKey, queryFn: () => fetchUserSessions(email), enabled: Boolean(email) });
    const sessions = data ?? [];
    const onSuccess = (title: string) => () => { addToast({ title, color: 'success' }); queryClient.invalidateQueries({ queryKey }); queryClient.invalidateQueries({ queryKey: ['admin-activity'] }); };
    const onError = (error: any) => addToast({ title: 'Failed', description: error?.response?.data?.message || error?.message || 'Please try again.', color: 'danger' });
    const removeOne = useMutation({ mutationFn: revokeSession, onSuccess: onSuccess('Session removed'), onError });
    const removeAll = useMutation({ mutationFn: () => revokeUserSessions(email), onSuccess: onSuccess('All sessions removed'), onError });

    return (
        <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-label="Active sessions" className="w-full max-w-xl rounded-[28px] bg-white p-6 shadow-2xl dark:bg-nightSurface">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Active sessions</h2>
                        <p className="truncate text-sm text-grey40 dark:text-nightMuted">{email}</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
                </div>
                <div className="max-h-[50vh] overflow-auto">
                    <SessionsList sessions={sessions.map(s => ({ ...s, current: false }))} isLoading={isLoading} busyId={removeOne.isPending ? removeOne.variables : null} onRevoke={id => removeOne.mutate(id)} />
                </div>
                <div className="mt-4 flex justify-end">
                    <button type="button" className={smallBtn} disabled={sessions.length === 0 || removeAll.isPending} onClick={() => window.confirm(`Remove all sessions for ${email}?`) && removeAll.mutate()}>Remove all sessions</button>
                </div>
            </div>
        </div>
    );
}
